import { makeAutoObservable } from 'mobx';
import type { AssetJSON } from 'src/new_arch/modules/projectView/models/asset';

import type { ImportEventData, MessagingService } from '../services/MessagingService/types';
import type BackgroundTask from './BackgroundTask';
import type { BackgroundTaskError, BackgroundTaskStatus } from './BackgroundTask';

export type OnAssetImportedCallback = ( asset: AssetJSON ) => void;
export type OnFinishedCallback = ( successCount: number, failedCount: number, durationMs: number ) => void;

export type ImportTaskParams = {
	id: string,
	messagingService: MessagingService,
	onAssetImported?: OnAssetImportedCallback,
	onFinished?: OnFinishedCallback
};

export default class ImportTask implements BackgroundTask {
	id: string;
	messagingService: MessagingService;
	onAssetImported?: OnAssetImportedCallback;
	onFinished?: OnFinishedCallback;
	status: BackgroundTaskStatus;
	assetsImported: number;
	assetsFailed: number;
	totalAssetsToImport: number;
	isObservingImport: boolean;
	createdAt: Date;
	errors: BackgroundTaskError[];

	static createAndObserve = ( params: ImportTaskParams ) => {
		const importTask = new ImportTask( params );
		importTask.observeImportFromBackend();
		return importTask;
	}

	constructor( { id, messagingService, onAssetImported, onFinished }: ImportTaskParams ) {
		this.id = id;
		this.messagingService = messagingService;
		this.onAssetImported = onAssetImported;
		this.onFinished = onFinished;

		this.status = 'unknown';
		this.assetsImported = 0;
		this.assetsFailed = 0;
		this.totalAssetsToImport = 0;
		this.isObservingImport = false;
		this.createdAt = new Date();
		this.errors = [];

		makeAutoObservable( this );
	}

	get cancelable() {
		return false;
	}

	get hasFinished() {
		return (
			this.status === 'successful' || this.status === 'partially_successful'
				|| this.status === 'failed' || this.status === 'canceled'
		);
	}

	get progressPercent() {
		if ( this.status === 'unknown' || this.totalAssetsToImport === 0 ) return 0;

		return ( this.count / this.total ) * 100;
	}

	get title() {
		return 'Importing Assets';
	}

	get count() {
		return this.assetsImported + this.assetsFailed;
	}

	get total() {
		return this.totalAssetsToImport;
	}

	get hasErrors() {
		return this.errors.length > 0;
	}

	get allFailed() {
		return this.assetsFailed === this.total;
	}

	get countText() {
		if ( this.status === 'successful' ) { return `Finished ${this.importedCountText}`; }
		if ( this.status === 'partially_successful' ) { return `Finished partially ${this.importedCountText}`; }
		if ( this.status === 'failed' ) return 'Failed Importing';
		if ( this.status === 'canceled' ) return 'Canceled';

		return `Processing ${this.partialCountText} files`;
	}

	get importedCountText() {
		return `${this.assetsImported}/${this.total}`;
	}

	get partialCountText() {
		return `${this.count}/${this.total}`;
	}

	observeImportFromBackend() {
		if ( this.isObservingImport ) return;

		this.messagingService.subscribeToImport( this.id, this.onImportEventReceived );
		this.isObservingImport = true;
	}

	cancel() {}

	private onImportEventReceived = ( data: ImportEventData ) => {
		const { status, imported, failed, total, asset, error } = data;

		this.status = this.mapToImportStatus( status );
		this.assetsImported = imported;
		this.assetsFailed = failed;
		this.totalAssetsToImport = total;

		if ( asset ) {
			this.onAssetImported?.( asset );
		}

		if ( error ) {
			this.errors.push( {
				identifier: error.filename,
				error: 'Error importing',
				message: error.message
			} );
		}

		if ( this.hasFinished ) {
			this.stopObservingImportFromBackend();

			const durationMs = new Date().getTime() - this.createdAt.getTime();
			this.onFinished?.( this.assetsImported, this.assetsFailed, durationMs );
		}
	}

	private mapToImportStatus( status: ImportEventData[ 'status' ] ) {
		if ( status === 'finished' ) { return this.getFinishedStatus(); }
		return status;
	}

	private getFinishedStatus() {
		if ( this.allFailed ) return 'failed';
		if ( this.hasErrors ) return 'partially_successful';
		return 'successful';
	}

	private stopObservingImportFromBackend() {
		if ( !this.isObservingImport ) return;

		this.messagingService.unsubscribeFromImport( this.id );
	}
}
